import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import { SERVICES } from "../data/services";
import { MultiStepLeadForm } from "../components/lead/MultiStepLeadForm";

export default function ServiceDetail() {
	const { id } = useParams();
	const service = SERVICES.find((s) => s.id === id);
	if (!service) return <Navigate to="/" replace />;
	const Icon = service.icon;

	return (
		<div className="pt-24">
			<section className="relative overflow-hidden py-14 md:py-20 bg-slate-50 dark:bg-slate-950/40">
				<div className="absolute -top-20 right-0 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
				<div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
					<div>
						<Link to="/#services" className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-emerald-600 mb-6">
							<ArrowLeft className="h-4 w-4" /> All services
						</Link>
						<span className="grid place-items-center h-14 w-14 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
							<Icon className="h-7 w-7" />
						</span>
						<span className="mt-5 inline-block rounded-full bg-amber-500/10 border border-amber-500/20 px-3 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
							{service.badge}
						</span>
						<h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight">
							{service.title}
						</h1>
						<p className="mt-3 text-xl text-emerald-700 dark:text-emerald-400 font-medium">{service.tagline}</p>
						<p className="mt-4 text-lg text-slate-600 dark:text-slate-300 max-w-lg">{service.desc}</p>

						<ul className="mt-8 space-y-3">
							{service.points.map((p) => (
								<li key={p} className="flex items-start gap-3">
									<span className="grid place-items-center h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-600 shrink-0 mt-0.5">
										<Check className="h-3.5 w-3.5" />
									</span>
									<span className="text-slate-700 dark:text-slate-300">{p}</span>
								</li>
							))}
						</ul>

						<div className="mt-8 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
							<p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">The outcome</p>
							<p className="mt-1 text-slate-700 dark:text-slate-300">{service.outcome}</p>
						</div>
					</div>

					<motion.div
						initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
						className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl shadow-slate-900/10 p-6 sm:p-8 lg:sticky lg:top-24"
					>
						<div className="mb-5">
							<p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
								Enquire about {service.title}
							</p>
							<h3 className="font-heading text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
								Get a free {service.title.toLowerCase()} plan
							</h3>
						</div>
						<MultiStepLeadForm
							embedded
							sourcePage={service.sourcePage}
							defaultRequirement={service.requirement}
						/>
					</motion.div>
				</div>
			</section>

			<section className="py-14">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="font-heading text-2xl font-bold text-slate-900 dark:text-white mb-6">Explore other services</h2>
					<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
						{SERVICES.filter((s) => s.id !== id).slice(0, 4).map((s) => (
							<Link key={s.id} to={`/services/${s.id}`}
								className="group rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 hover:border-emerald-400 hover:-translate-y-1 transition-all">
								<s.icon className="h-6 w-6 text-emerald-600" />
								<h3 className="mt-3 font-semibold text-slate-900 dark:text-white flex items-center justify-between">
									{s.title} <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500" />
								</h3>
							</Link>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
